import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../components/common/Dropdown";
import Button from "../../../components/ui/Button";
import { getCompanyFiles } from "../../../api/companyFileCategories";
import { CompanyFileListItem } from "../../../types/requestsOutputs";
import { Tooltip } from "@mui/material";
import styles from "./FilesTab.module.css";

interface FilesTabProps {
    name: string;
    onAddDatabaseReference: (fileId: string, fileName: string) => void;
    onDropFiles: (files: File[]) => void;
    fileItemsCount?: number;
    onMultipleFilesError?: (message: string) => void;
}

const FilesTab: React.FC<FilesTabProps> = ({
    name,
    onAddDatabaseReference,
    onDropFiles,
    fileItemsCount = 0,
    onMultipleFilesError,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedFileId, setSelectedFileId] = useState("");
    const [databaseFiles, setDatabaseFiles] = useState<CompanyFileListItem[]>([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const dragCounterRef = useRef(0);

    useEffect(() => {
        const fetchFiles = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;

            setLoadingFiles(true);
            try {
                const response = await getCompanyFiles({
                    token,
                    queryParams: {
                        limit: 100,
                        page: 1,
                    },
                });
                if (response.success && response.data) {
                    setDatabaseFiles(response.data);
                }
            } catch (error) {
                console.error("Error fetching database files:", error);
            } finally {
                setLoadingFiles(false);
            }
        };

        fetchFiles();
    }, []);

    const fileOptions = databaseFiles.map((file) => ({
        value: file.id,
        label: file.name || file.title || file.url || `File ${file.id}`,
    }));

    const handleAddDatabaseFile = useCallback(() => {
        if (selectedFileId) {
            // Check if max files reached
            const isMaxReached = fileItemsCount >= 3;
            if (isMaxReached) {
                return;
            }
            const selectedFile = databaseFiles.find((f) => f.id === selectedFileId);
            if (selectedFile) {
                // Directly add to final list, bypassing dropzone
                const fileName =
                    selectedFile.name || selectedFile.title || selectedFile.url || `File ${selectedFile.id}`;
                onAddDatabaseReference(selectedFile.id, fileName);
                setSelectedFileId("");
            }
        }
    }, [selectedFileId, databaseFiles, fileItemsCount, onAddDatabaseReference]);

    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

    const handleFiles = useCallback(
        (files: FileList | File[]) => {
            const filesArray = Array.isArray(files) ? files : Array.from(files);
            if (!filesArray.length) return;

            // Only accept one file at a time
            if (filesArray.length > 1) {
                if (onMultipleFilesError) {
                    onMultipleFilesError(t("brandVoices.onlyOneFileAtATime") || "Only one file can be added at a time");
                }
                return;
            }

            const file = filesArray[0];

            // Check file size - parent component will show error for oversized files
            if (file.size > MAX_FILE_SIZE) {
                return; // Parent will handle the error
            }

            // Only allow one file in the dropzone at a time
            setSelectedFiles([file]);
        },
        [MAX_FILE_SIZE, onMultipleFilesError, t],
    );

    const handleAddFiles = useCallback(() => {
        if (selectedFiles.length > 0) {
            onDropFiles(selectedFiles);
            setSelectedFiles([]);
        }
    }, [selectedFiles, onDropFiles]);

    const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current++;
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        if (dragCounterRef.current === 1) {
            setDragActive(true);
        }
    }, []);

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.stopPropagation();
            dragCounterRef.current = 0;
            setDragActive(false);
            if (event.dataTransfer?.files?.length) {
                handleFiles(event.dataTransfer.files);
            }
        },
        [handleFiles],
    );

    return (
        <div className={styles.container}>
            <label className={styles.label}>{t("brandVoices.databaseLabel")}</label>
            <div className={styles.databaseSection}>
                <Dropdown
                    label=""
                    options={fileOptions}
                    value={selectedFileId}
                    onChange={setSelectedFileId}
                    placeholder={t("brandVoices.databasePlaceholder")}
                    loading={loadingFiles}
                    error={false}
                    isWithoutMargin
                />
                <Tooltip
                    title={
                        fileItemsCount >= 3
                            ? t("brandVoices.maxFilesReached") || "Maximum of 3 files reached"
                            : !selectedFileId
                              ? t("brandVoices.selectFileFirst") || "Please select a file first"
                              : ""
                    }
                    arrow
                >
                    <span>
                        <Button
                            variant="primary"
                            label={t("brandVoices.addDatabaseFileButton")}
                            onClick={handleAddDatabaseFile}
                            disabled={!selectedFileId || fileItemsCount >= 3}
                        />
                    </span>
                </Tooltip>
            </div>

            <section>
                <h3 className={styles.documentsTitle}>{t("brandVoices.documentsLabel")}</h3>
                <Tooltip title={t("brandVoices.maxFileSizeTooltip", { size: "20MB" })} arrow placement="top">
                    <div
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => inputRef.current?.click()}
                        className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ""}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                inputRef.current?.click();
                            }
                        }}
                    >
                        <div className={styles.dropzoneText}>
                            {t("brandVoices.dragDropOrBrowse")}{" "}
                            <span className={styles.browseLink}>{t("brandVoices.browse")}</span>
                        </div>
                        <div className={styles.dropzoneIconContainer}>
                            <div className={styles.dropzoneIcon}>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M14 2V8H20"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <Tooltip
                                title={
                                    <div className={styles.tooltipContent}>
                                        <div className={styles.tooltipTitle}>{t("brandVoices.doc")}</div>
                                        <div className={styles.tooltipRow}>
                                            <strong>{t("brandVoices.fileTypes")}:</strong>{" "}
                                            {t("brandVoices.acceptedFileTypes")}
                                        </div>
                                        <div className={styles.tooltipRow}>
                                            <strong>{t("brandVoices.maxSize")}:</strong> 20MB
                                        </div>
                                    </div>
                                }
                                arrow
                                placement="top"
                            >
                                <div className={styles.docLabel}>DOC</div>
                            </Tooltip>
                        </div>
                    </div>
                </Tooltip>
                <input
                    ref={inputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={(event) => {
                        if (event.target.files) {
                            handleFiles(event.target.files);
                            event.target.value = "";
                        }
                    }}
                />
                {selectedFiles.length > 0 && (
                    <ul className={styles.fileList}>
                        {selectedFiles.map((file, index) => (
                            <li key={`uploaded-${file.name}-${index}`}>{file.name}</li>
                        ))}
                    </ul>
                )}
                <div className={styles.actions}>
                    <Tooltip
                        title={(() => {
                            const isMaxReached = fileItemsCount >= 3;
                            if (isMaxReached) {
                                return t("brandVoices.maxFilesReached") || "Maximum of 3 files reached";
                            }
                            if (!name.trim() && selectedFiles.length === 0) {
                                return t("brandVoices.missingNameAndFiles");
                            }
                            if (!name.trim()) {
                                return t("brandVoices.missingName");
                            }
                            if (selectedFiles.length === 0) {
                                return t("brandVoices.missingFiles");
                            }
                            return "";
                        })()}
                        arrow
                    >
                        <span>
                            <Button
                                variant="primary"
                                label={t("brandVoices.addFilesButton")}
                                onClick={handleAddFiles}
                                disabled={selectedFiles.length === 0 || !name.trim() || fileItemsCount >= 3}
                            />
                        </span>
                    </Tooltip>
                </div>
            </section>
        </div>
    );
};

export default FilesTab;
